import { PaginationInfo } from '../shared';
import { graphqlApiWithPersisted } from './graphqlApi';
import { LeaderBoardUserBadge, User } from './user';

interface LeaderBoardUsersFilters {
  from: string;
  to: string;
}

interface LeaderBoardUsersArgs {
  filters: LeaderBoardUsersFilters;
  page?: number;
  size?: number;
}

interface LeaderBoardUser {
  position: number;
  positionDiff: number;
  totalPoints: number;
  user: User;
}

interface LeaderBoardUsersData {
  items: LeaderBoardUser[];
  pageInfo: PaginationInfo;
}

interface LeaderBoardUsersResponse {
  data: { leaderBoardUsers: LeaderBoardUsersData };
}

export interface LeaderBoardMeFilters {
  from: string;
  to: string;
}

interface LeaderBoardMe {
  points: number;
  position: number;
  positionDiff: number;
  totalPoints: number;
}

interface LeaderBoardMeResponse {
  data: { leaderBoardMe: LeaderBoardMe };
}

interface LeaderBoardMeArgs {
  filters: LeaderBoardMeFilters;
}

interface LeaderBoardGenerateCSVReportArgs {
  from: string;
  to: string;
}

interface LeaderBoardBadgesResponse {
  data: {
    leaderBoardBadges: {
      items: LeaderBoardUserBadge[];
      pageInfo: PaginationInfo;
    };
  };
}

interface LeaderBoardGenerateCSVReportResponse {
  data: {
    leaderBoardGenerateCSVReport: { payload: boolean };
  };
}

export const leaderBoardApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    leaderBoardMe: builder.query<LeaderBoardMe, LeaderBoardMeArgs>({
      query: ({ filters }) => ({
        url: '/graphql/webapp?leaderBoardMe',
        method: 'POST',
        body: {
          query: `query LeaderBoardMe($filters: GetLeaderBoardFiltersArgs!) {
            leaderBoardMe(filters: $filters) {
              points
              position
              positionDiff
              totalPoints
            }
          }`,
          variables: { filters },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: LeaderBoardMeResponse) => response.data.leaderBoardMe,
    }),
    leaderBoardUsers: builder.query<LeaderBoardUsersData, LeaderBoardUsersArgs>({
      query: ({ filters, page = 1, size = 15 }) => ({
        url: '/graphql/webapp?leaderBoardUsers',
        method: 'POST',
        body: {
          query: `query LeaderBoardUsers($filters: GetLeaderBoardFiltersArgs!, $page: Int, $size: Int) {
            leaderBoardUsers(filters: $filters, page: $page, size: $size) {
              items {
                position
                positionDiff
                totalPoints
                user {
                  id
                  screenName
                  avatar {
                    url
                  }
                  leaderBoardBadge {
                    id
                    level
                    name
                    nameRender
                  }
                }
              }
              pageInfo {
                page
                hasNextPage
                hasPreviousPage
                totalPages
              }
            }
          }`,
          variables: { filters, page, size },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: LeaderBoardUsersResponse) => response.data.leaderBoardUsers,
    }),
    leaderBoardBadges: builder.query<LeaderBoardUserBadge[], void>({
      query: () => ({
        url: '/graphql/webapp?leaderBoardBadges',
        method: 'POST',
        body: {
          query: `query LeaderBoardBadges {
            leaderBoardBadges {
              items {
                id
                level
                name
                nameRender
              }
            }
          }`,
        },
      }),
      transformResponse: (response: LeaderBoardBadgesResponse) =>
        response.data.leaderBoardBadges.items,
    }),
    leaderBoardGenerateCSVReport: builder.mutation<boolean, LeaderBoardGenerateCSVReportArgs>({
      query: ({ from, to }) => ({
        url: '/graphql/webapp?leaderBoardGenerateCSVReport',
        method: 'POST',
        body: {
          query: `mutation LeaderBoardGenerateCSVReport($input: LeaderBoardGenerateCSVReportInput!) {
            leaderBoardGenerateCSVReport(input: $input) {
              payload
            }
          }`,
          variables: { input: { from, to } },
        },
      }),
      transformResponse: (response: LeaderBoardGenerateCSVReportResponse) =>
        response.data.leaderBoardGenerateCSVReport.payload,
    }),
  }),
});

export const { useLeaderBoardMeQuery, useLeaderBoardUsersQuery, useLeaderBoardBadgesQuery } =
  leaderBoardApi;
